// `?q=<text>` deep-link capture + drain (REM-3 / M6).
//
// The convenience: landing on `app.drishalom.com/?q=<urlencoded text>` carries a
// prompt into the chat. SECURITY: the drained text is **never auto-sent**. A
// crafted `?q=<attacker text>` link would otherwise dispatch attacker-controlled
// text as the victim's first post-auth message, and their agent would act on it
// (a phishing / prompt-injection primitive). The operator-decided fix:
// the drain RETURNS the text for the caller to PREFILL the composer, and the
// user explicitly taps send. `drainPendingQ` has no send path by construction.
//
// The capture runs before any identity is known, so it writes the unscoped
// capture key; `claimIdentity` moves it under the signing-in identity's own key
// — or drops it — and the drain reads only that scoped key. Whose key is whose:
// identityKeys.ts.
//
// Split out of App.tsx as two pure functions so both the capture (runs once at
// module load, before React mounts) and the drain (runs post-hydration) are
// unit-testable without rendering the whole App.

import { PENDING_Q_CAPTURE_KEY, pendingQKeyFor } from './identityKeys.ts';

// The pre-identity capture key. Re-exported from its one home so the capture
// and the App's own references read from the same place.
export { PENDING_Q_CAPTURE_KEY };

/**
 * Capture the `?q=` param at module load, BEFORE React renders, so the value
 * survives the magic-link auth redirect (`/<shortcode>` → 302 → `/`). Stashes
 * the trimmed-non-empty value (truncated to `maxLen`) into storage and strips
 * the `q` param from the URL via `history.replaceState`. SSR / disabled-storage
 * safe — any throw is swallowed (no-op).
 *
 * Pure-ish: every dependency is injected so a test can pass fakes. Defaults to
 * the live `window` / `localStorage` for the production call site.
 */
export function capturePendingQ(
  win: Pick<Window, 'location' | 'history'> = window,
  storage: Pick<Storage, 'setItem'> = localStorage,
  maxLen = 10_000,
): void {
  try {
    const params = new URLSearchParams(win.location.search);
    const q = params.get('q');
    if (q && q.trim()) {
      storage.setItem(PENDING_Q_CAPTURE_KEY, q.slice(0, maxLen));
      params.delete('q');
      const qs = params.toString();
      const url = win.location.pathname + (qs ? '?' + qs : '') + win.location.hash;
      win.history.replaceState(null, '', url);
    }
  } catch {
    /* SSR / disabled storage — ignore */
  }
}

/**
 * Drain the stashed `?q=` value post-hydration: read it, clear it, and RETURN it
 * (or `null` when nothing is pending) for the caller to prefill into the
 * composer. Never sends — returning the text (instead of dispatching it) is the
 * whole security fix. The single-fire guard lives at the call site.
 *
 * Reads `userId`'s own key alone, so a stash the previous person left on this
 * browser is never drained into this composer.
 */
export function drainPendingQ(
  userId: string,
  storage: Pick<Storage, 'getItem' | 'removeItem'> = localStorage,
): string | null {
  const key = pendingQKeyFor(userId);
  const q = storage.getItem(key);
  if (q && q.trim()) {
    storage.removeItem(key);
    return q;
  }
  return null;
}
